import React, { useState, useEffect } from 'react';
import './ExportPage.css';

const ExportPage = () => {
  const [tables, setTables] = useState([]);
  const [selectedTables, setSelectedTables] = useState([]);
  const [format, setFormat] = useState('json');
  const [isLoading, setIsLoading] = useState(false);
  const [exportResult, setExportResult] = useState(null);
  const [error, setError] = useState(null);
  const [loadingTables, setLoadingTables] = useState(true);
  const [packages, setPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [packagesError, setPackagesError] = useState(null);

  // Fonction pour valider la structure des packages
  const validatePackagesStructure = (packagesData) => {
    if (!packagesData || typeof packagesData !== 'object') {
      throw new Error('Le fichier JSON doit contenir un objet');
    }

    if (!Array.isArray(packagesData.packages)) {
      throw new Error('La propriété "packages" doit être un tableau');
    }

    // Vérifier chaque package
    packagesData.packages.forEach((pkg, index) => {
      if (!pkg.id || typeof pkg.id !== 'string') {
        throw new Error(`Package ${index + 1}: la propriété "id" est requise et doit être une chaîne`);
      }

      if (!pkg.name || typeof pkg.name !== 'string') {
        throw new Error(`Package ${index + 1}: la propriété "name" est requise et doit être une chaîne`);
      }

      if (!pkg.description || typeof pkg.description !== 'string') {
        throw new Error(`Package ${index + 1}: la propriété "description" est requise et doit être une chaîne`);
      }

      if (!Array.isArray(pkg.tables) || pkg.tables.length === 0) {
        throw new Error(`Package ${index + 1}: la propriété "tables" est requise et doit être un tableau non vide`);
      }

      // Vérifier que toutes les tables référencées existent
      pkg.tables.forEach((tableName) => {
        if (typeof tableName !== 'string') {
          throw new Error(`Package ${index + 1}: les noms de tables doivent être des chaînes de caractères`);
        }
      });
    });
    return true;
  };

  // Charger les tables et les packages
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingTables(true);
        setLoadingPackages(true);

        // Charger les tables depuis le backend
        const tablesResponse = await fetch('/api/tables');

        if (!tablesResponse.ok) {
          throw new Error(`Erreur ${tablesResponse.status}: ${await tablesResponse.text()}`);
        }

        const tablesData = await tablesResponse.json();

        if (tablesData.success && tablesData.tables) {
          setTables(tablesData.tables);
          // console.log('Tables disponibles:', tablesData.tables);

          // Par défaut, sélectionner la table Mot si elle existe
          // const motTable = tablesData.tables.find((t) => t.name === 'Mots');
          // if (motTable) {
          //   setSelectedTables(['Mots']);
          // }
        } else {
          throw new Error('Format de réponse inattendu pour les tables');
        }

        // Charger les packages depuis l'API backend
        try {
          let packagesData;
          const packagesResponse = await fetch('/api/export-packages');

          if (packagesResponse.ok) {
            packagesData = await packagesResponse.json();
          } else {
            throw new Error(`Erreur ${packagesResponse.status}: ${await packagesResponse.text()}`);
          }

          // Vérifier que le JSON contient bien des packages
          if (!packagesData.packages || !Array.isArray(packagesData.packages) || packagesData.packages.length === 0) {
            console.warn('⚠️ Le fichier JSON ne contient aucun package ou la structure est invalide');
            setPackages([]);
            setPackagesError('Aucun package défini dans le fichier JSON');
            return;
          }

          // Valider la structure des packages
          validatePackagesStructure(packagesData);

          // Vérifier que toutes les tables des packages existent
          // console.log('Packages chargés:', packagesData.packages);
          //
          const validPackages = packagesData.packages.map((pkg) => {
            const validTables = pkg.tables.filter((tableName) => tablesData.tables.some((t) => t.name === tableName));

            if (validTables.length !== pkg.tables.length) {
              console.warn(`⚠️ Package "${pkg.name}": certaines tables ne sont pas disponibles`);
            }

            return {
              ...pkg,
              validTables,
              hasInvalidTables: validTables.length !== pkg.tables.length,
            };
          });

          // console.log('✅ Packages valides:', validPackages); // ✅ Ici

          setPackages(validPackages);
          // console.log(`✅ ${validPackages.length} packages chargés avec succès`);
        } catch (packagesErr) {
          console.error('Erreur lors du chargement des packages:', packagesErr);
          setPackagesError('Impossible de charger les packages: ' + packagesErr.message);
          setPackages([]); // S'assurer que packages est un tableau vide en cas d'erreur
        }
      } catch (err) {
        console.error('Erreur lors du chargement des données:', err);
        setError('Impossible de charger les données: ' + err.message);

        // Utiliser une liste par défaut en cas d'erreur
        let fallbackTables = [];
        try {
          const response = await fetch('/api/tables');
          if (response.ok) {
            const data = await response.json();
            if (data.success && data.tables) {
              fallbackTables = data.tables;
            }
          }
        } catch (error) {
          console.error('Erreur lors de la génération de fallbackTables:', error);
        }

        setTables(fallbackTables);
        setSelectedTables(['Mots']);
      } finally {
        setLoadingTables(false);
        setLoadingPackages(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    // Réinitialiser le compte rendu lorsqu'on change la sélection
    setExportResult(null);
    setError(null);
  }, [selectedTables, format]);

  const handleTableSelect = (tableName) => {
    setSelectedTables((prev) => {
      if (prev.includes(tableName)) {
        return prev.filter((t) => t !== tableName);
      } else {
        return [...prev, tableName];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedTables.length === tables.length) {
      setSelectedTables([]);
    } else {
      setSelectedTables(tables.map((t) => t.name));
    }
  };

  const handlePackageSelect = (pkg) => {
    // Si le package contient des tables invalides, ne pas le sélectionner
    if (pkg.hasInvalidTables) {
      setError(`Le package "${pkg.name}" contient des tables non disponibles`);
      return;
    }

    // Sélectionner uniquement les tables de ce package
    setSelectedTables(pkg.validTables);
  };

  const handleFormatChange = (e) => {
    setFormat(e.target.value);
  };

  const handleExport = async () => {
    if (selectedTables.length === 0) {
      setError('Veuillez sélectionner au moins une table à exporter.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setExportResult(null);

    try {
      // Appeler l'API backend pour l'export
      const response = await fetch('/api/export', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tables: selectedTables,
          format: format,
        }),
      });

      if (!response.ok) {
        throw new Error(`Erreur ${response.status}: ${await response.text()}`);
      }

      const result = await response.json();
      setExportResult(result);
    } catch (err) {
      console.error("Erreur lors de l'export:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const getExportName = () => {
    const now = new Date();
    const dateStr = `${String(now.getDate()).padStart(2, '0')}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getFullYear()).slice(-2)}`;
    const timeStr = `${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
    const tablesName = selectedTables.length === tables.length ? 'all' : selectedTables.join('-');

    return `export-${dateStr}-${timeStr}-${format}-${tablesName}`;
  };

  return (
    <div className="export-page">
      <h1>📦 Export de la Base de Données</h1>

      <div className="export-options">
        <div className="tables-section">
          <h2>📋 Sélection des Tables</h2>
          {loadingTables ? (
            <div className="loading-tables">
              <span className="loading-spinner"></span>
              Chargement des tables...
            </div>
          ) : (
            <>
              <div className="table-select-controls">
                <button onClick={handleSelectAll} className={`select-all-btn ${selectedTables.length === tables.length ? 'deselect' : ''}`}>
                  {selectedTables.length === tables.length ? 'Désélectionner tout' : 'Tout sélectionner'}
                </button>
                <span className="selected-count">{selectedTables.length} table(s) sélectionnée(s)</span>
              </div>
              <div className="tables-grid">
                {tables.map((table) => (
                  <label key={table.id} className="table-checkbox">
                    <input type="checkbox" checked={selectedTables.includes(table.name)} onChange={() => handleTableSelect(table.name)} />
                    <span className="checkbox-custom"></span>
                    <span className="table-label">{table.label}</span>
                    <span className="table-name">({table.name})</span>
                  </label>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="packages-section">
          <h2>📦 Packages Prédéfinis</h2>
          {loadingPackages ? (
            <div className="loading-packages">
              <span className="loading-spinner"></span>
              Chargement des packages...
            </div>
          ) : packagesError ? (
            <div className="packages-error">⚠️ {packagesError}</div>
          ) : packages.length > 0 ? (
            <div className="packages-grid">
              {packages.map((pkg) => (
                <button
                  key={pkg.id}
                  onClick={() => handlePackageSelect(pkg)}
                  className={`package-btn ${pkg.hasInvalidTables ? 'invalid' : ''}`}
                  disabled={pkg.hasInvalidTables}
                  title={pkg.hasInvalidTables ? 'Certaines tables de ce package ne sont pas disponibles' : pkg.description}
                >
                  <span className="package-name">{pkg.name}</span>
                  <span className="package-tables-count">
                    ({pkg.validTables.length} table{pkg.validTables.length > 1 ? 's' : ''})
                  </span>
                  {pkg.hasInvalidTables && <span className="package-warning">⚠️</span>}
                </button>
              ))}
            </div>
          ) : (
            <div className="no-packages">
              {packagesError ? (
                <>
                  <p>⚠️ {packagesError}</p>
                  <p style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '10px' }}>
                    Vérifiez que le fichier <code>public/data/export-packages.json</code> existe et contient des packages valides.
                  </p>
                </>
              ) : (
                <p>Aucun package défini</p>
              )}
            </div>
          )}
        </div>

        <div className="format-section">
          <h2>📝 Format de Sortie</h2>
          <div className="format-options">
            <label>
              <input type="radio" value="json" checked={format === 'json'} onChange={handleFormatChange} />
              <span className="radio-custom"></span>
              JSON (recommandé)
            </label>

            <label>
              <input type="radio" value="csv" checked={format === 'csv'} onChange={handleFormatChange} />
              <span className="radio-custom"></span>
              CSV
            </label>

            <label>
              <input type="radio" value="json-structure" checked={format === 'json-structure'} onChange={handleFormatChange} />
              <span className="radio-custom"></span>
              JSON Structure
            </label>
          </div>

          <div className="export-info">
            <h3>📊 Informations sur l'export:</h3>
            <p>
              <strong>Nom de l'export:</strong> {getExportName()}
            </p>
            <p>
              <strong>Format:</strong> {format.toUpperCase()}
            </p>
            <p>
              <strong>Tables:</strong> {selectedTables.join(', ')}
            </p>
          </div>

          <button onClick={handleExport} disabled={isLoading || selectedTables.length === 0} className="export-btn">
            {isLoading ? (
              <>
                <span className="loading-spinner"></span>
                Export en cours...
              </>
            ) : (
              '📥 Exporter la Base de Données'
            )}
          </button>
        </div>
      </div>

      {error && <div className="error-message">❌ {error}</div>}

      {exportResult && (
        <div className="export-result">
          <h2>✅ Export Réussi !</h2>
          <div className="result-details">
            <p>
              <strong>Dossier:</strong> {exportResult.sessionDir}
            </p>
            <p>
              <strong>Archive:</strong> {exportResult.globalZip}
            </p>
            <p>
              <strong>Tables exportées:</strong> {exportResult.results.length}
            </p>
            <p>
              <strong>Total enregistrements:</strong> {exportResult.totalRecords}
            </p>
            <p>
              <strong>Format:</strong> {exportResult.format.toUpperCase()}
            </p>
          </div>

          <div className="result-actions">
            {/* <button 
              onClick={() => window.open(`/exports/${exportResult.globalZip}`, '_blank')}
              className="download-btn"
            >
              💾 Télécharger l'archive
            </button> */}

            <button
              onClick={async () => {
                try {
                  setIsLoading(true);
                  const response = await fetch(`/api/export/download/${exportResult.globalZip}`);
                  const blob = await response.blob();
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = exportResult.globalZip;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  window.URL.revokeObjectURL(url);
                } catch (error) {
                  console.error('Erreur lors du téléchargement:', error);
                  setError('Échec du téléchargement: ' + error.message);
                } finally {
                  setIsLoading(false);
                }
              }}
              className="download-btn"
            >
              💾 Télécharger l'archive
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExportPage;
